<template>
  <div class="form-control rounded-lg ds-input ec-ds-search">
    <input type="text" class="form-control w-100" :aria-label="$t(config.textKeys.textFieldPlaceholder)"
      :placeholder="$t(config.textKeys.textFieldPlaceholder)" :value="getQuery"
      @input="event => setQuery(event.target.value)" @keyup="handleKeyup($event)"
      @click="showSuggestions.active = true;" @keydown.down.prevent="handleArrowDown"
      @keydown.up.prevent="handleArrowUp" @keydown.enter="handleEnter">
    <button class="filter-btn" 
    :class="{ 
      'filter-btn-active': filterActive ,
      'bounce-animation': shouldBounce
    }"
    @click="toggleFilter">
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" class="bi bi-filter"
        viewBox="0 0 16 16">
        <path
          d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5m-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5m-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5" />
      </svg>
      {{ $t('message.datasetFacets.title') }}
    </button>
 
    <ul ref="dropdownSuggestions" v-if="showSuggestions.active && isLoadingSuggestions" type="text"
      class="suggestedTitlesWrapper">
      <li v-for="i in 2" :key="i" class="loadingField">load</li>
    </ul>
    <ul ref="dropdownSuggestions" v-if="showSuggestions.active && !isLoadingSuggestions" type="text"
      class="suggestedTitlesWrapper">
      <li v-for="(item, index) in suggestedTitles" :key="index" :class="{ 'focused': currentFocusIndex === index }">
        <a v-if="isEditorialItem(item)" :href="autocompletePath(item)" class="link">
          <div class="auto-suggestion-container">
            <div v-if="editorialThumbnail(item)" class="image-container">
              <img :src="editorialThumbnail(item)" :alt="item.title"
                class="auto-image-resource">
            </div>
            <div v-else class="auto-thumbnail">
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="black" class="bi bi-archive"
                  viewBox="0 0 16 16">
                  <path
                    d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 12.5V5a1 1 0 0 1-1-1zm2 3v7.5A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5V5zm13-3H1v2h14zM5 7.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5" />
                </svg>
              </span>
            </div>
            <div class="auto-content">
              <div class="auto-title">
                {{ item.title[language] }}
                <span v-if="item.issued">({{ getYear(item.issued) }})</span>
              </div>
              <div class="auto-type">Editorial Content</div>
            </div>
          </div>
        </a>

        <router-link v-else :to="autocompletePath(item)" class="link">
          <div class="auto-suggestion-container">
            <div v-if="item.country" class="image-container">
              <img :src="getFlagImage(item.country.id)" :alt="`${item.country.id}-flag`" class="auto-image">
            </div>

            <div v-else class="auto-thumbnail">
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="black" class="bi bi-archive"
                  viewBox="0 0 16 16">
                  <path
                    d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 12.5V5a1 1 0 0 1-1-1zm2 3v7.5A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5V5zm13-3H1v2h14zM5 7.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5" />
                </svg>
              </span>
            </div>
            <div class="auto-content">
              <div class="auto-title">
                {{ item.title[language] }}
                <span v-if="item.issued">({{ getYear(item.issued) }})</span>
              </div>
              <div class="auto-type">{{ item.index }}</div>
            </div>
          </div>
        </router-link>
      </li>
    </ul>
    <!-- <app-link v-if="config.textFieldHelpLink" :to="config.textFieldHelpLink" target="_blank" isTooltip="true">
      <i class="tooltip-icon material-icons small-icon align-right absolute text-dark pl-1" data-placement="right">
        help_outline
      </i> 
    </app-link> -->

    <i v-if="config.textFieldHelpLink" class="tooltip-icon material-icons small-icon align-right text-dark pl-1"
      data-toggle="tooltip" data-placement="top" data-html="true" data-trigger="hover" :title="tooltipContent">
      help_outline
    </i>
  </div>
</template>

<script>
import { reactive, ref } from "vue";
import { onClickOutside } from "@vueuse/core/index";
import { helpers } from "@piveau/piveau-hub-ui-modules";
import $ from "jquery";
import { mapActions, mapGetters } from 'vuex';
import { getEditorialThumbnailOrFlag } from "@/utils/editorialContentThumbnail";

const { getCountryFlagImg } = helpers;


export default {
  name: "SearchBarTextField",
  setup() {
    const dropdownSuggestions = ref('dropdownSuggestions');
    const showSuggestions = reactive({
      active: false,
    });
    onClickOutside(dropdownSuggestions, () => {
      showSuggestions.active = false;
    });
    return {
      showSuggestions,
      onClickOutside,
    };
  },
  props: {
    config: Object,
    changeQuery: Function
  },
  components: {
  },
  data() {
    return {
      isLoadingSuggestions: false,
      lastKeyup: null,
      baseUrl: this.$env.api.baseUrl,
      suggestedTitles: [],
      suggestedTitlesCatalogue: [],
      suggestedTitlesCombined: [],
      language: this.$route.query.locale || "en",
      currentFocusIndex: -1,
      filterCollapsed: false,
      mobileFiltersOpen: false,
      shouldBounce: false,
    }
  },
  computed: {
     ...mapGetters('search', ['getShowFilter']),
    filterActive() {
       if (window.innerWidth <= 790) {
      // Auf Mobile: prüfe den lokalen State
      return this.mobileFiltersOpen;
      }
      // Auf Desktop: verwende Vuex Store
      return this.getShowFilter;
    },
    getQuery() {
      return this.$store.getters[`${this.config.getQuery[0]}/${this.config.getQuery[1]}`];
    },
    tooltipContent() {
      return `Enter one or more search terms. Use AND to find all terms in random order, OR to find at least one term, "" to find an "exact phrase" and () to combine search elements (e.g. (environment OR nature) AND (refurbish OR reuse). Append an asterisk (*) to a search term to find variations of it (transp*, environm*). <br><br> <a href="${this.config.textFieldHelpLink}" target="_blank" class="tooltip-link">Get more details</a><br>`;
    },
  },
  methods: {
   ...mapActions('search', ['toggleShowFilter']),
   toggleFilter() {
    this.shouldBounce = false;
    if (window.innerWidth <= 790) {
      // On mobile, toggle the Bootstrap collapse
      const facetsElement = document.getElementById('mobileDatasetFacets');
      if (facetsElement) {
        $(facetsElement).collapse('toggle');
        // Toggle den lokalen State
        this.mobileFiltersOpen = !this.mobileFiltersOpen;
      }
    } else {
      // On desktop, use Vuex store
      this.toggleShowFilter();
    }
  },
  setCookie(name, value, days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    },
    getCookie(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for(let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },
    editorialThumbnail(item) {
      return getEditorialThumbnailOrFlag(item, getCountryFlagImg);
    },
    handleKeyup(event) {
      const ignoredKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'];
      if (!ignoredKeys.includes(event.key)) {
        this.autocompleteSuggestions();
      }
    },
    autocompletePath(item) {
      if (item.index === 'dataset') return `/datasets/${item.id}`;
      if (item.index === 'catalogue') return `/catalogues/${item.id}`;
      if (item.index === 'dataservice') return `/dataservices/${item.id}`;
      if (item.index === 'series') return `/datasetseries/${item.id}`;
      if (item.index === 'resource_editorial-content' || item.index === 'resource') return item.path;
    },
    isEditorialItem(item) {
      return item.index === 'resource_editorial-content' || item.index === 'resource';
    },
    handleArrowDown() {
      if (this.showSuggestions.active) {
        const maxIndex = this.config.scope === 'catalogue'
          ? this.suggestedTitlesCatalogue.length - 1
          : this.suggestedTitles.length - 1;
        this.currentFocusIndex = this.currentFocusIndex < maxIndex ? this.currentFocusIndex + 1 : 0;
      }
    },
    handleArrowUp() {
      if (this.showSuggestions.active) {
        const maxIndex = this.config.scope === 'catalogue'
          ? this.suggestedTitlesCatalogue.length - 1
          : this.suggestedTitles.length - 1;
        this.currentFocusIndex = this.currentFocusIndex > 0 ? this.currentFocusIndex - 1 : maxIndex;
      }
    },
   handleEnter() {
      if (this.currentFocusIndex >= 0) {
        // Navigate to the selected suggestion
        const selectedItem = this.config.scope === 'catalogue'
          ? this.suggestedTitlesCatalogue[this.currentFocusIndex]
          : this.suggestedTitles[this.currentFocusIndex];
        const path = this.autocompletePath(selectedItem);
        if (path) this.$router.push(path);
      } else {
        this.changeQuery()
      }
    },
    getYear(date) {
      return date ? date.substring(0, 4) : '';
    },
    getFlagImage(id) {
      const europeSpatialUri = 'http://publications.europa.eu/resource/authority/continent/EUROPE';
      const normalizedId = id === europeSpatialUri || id?.toLowerCase() === 'europe' ? 'eu' : id;
      return getCountryFlagImg(normalizedId);
    },
    setQuery(query) {
      this.$store.dispatch(`${this.config.setQuery[0]}/${this.config.setQuery[1]}`, query);
    },
    async autocompleteSuggestions() {
      if (!this.showSuggestions.active) return;

      this.isLoadingSuggestions = true;

      // Nur die letzte Eingabe berücksichtigen, um die Leistung zu verbessern
      clearTimeout(this.lastKeyup);
      this.lastKeyup = setTimeout(async () => {
        let endpoint = this.baseUrl;

        let autocompleteRequest;
        switch (this.config.scope) {
          case 'dataset':
            autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery + "&filters=" + "dataset" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",description." + this.language + ",keywords,country,issued,id" +
              "&limit=10";
            break;
          case 'catalogue':
            autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery + "&filters=" + "catalogue" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",country,id" +
              "&limit=10";
            break;
          case 'combined':
            autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery +
              "&filters=" + "catalogue,dataset,dataservice,datasetseries,resource" +
              "&resource=editorial-content" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",country,id,path,thumbnail,content_type" +
              "&limit=10";
            break;
          case 'editorialContent':
            autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery +
              "&filters=" + "resource" +
              "&resource=editorial-content" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",country,id,path,thumbnail,content_type" +
              "&limit=10";
            break;
          case 'dataservice':
             autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery +
              "&filters=" + "dataservice" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",id,path,thumbnail,content_type" +
              "&limit=10";
            break;
          case 'datasetseries':
             autocompleteRequest = endpoint + "/search" + "?q=" + this.getQuery +
              "&filters=" + "datasetseries" +
              "&autocomplete=true" +
              "&includes=title." + this.language + ",id,path,thumbnail,content_type" +
              "&limit=10";
            break;
        }

        try {
          const response = await fetch(autocompleteRequest);
          const data = await response.json();
          const results = data?.result?.results || [];
          const titles = results.flatMap((result) => Array.isArray(result?.titles) ? result.titles : []);
          this.suggestedTitles = titles.map((item) => ({
            ...item,
            index: item.index === 'resource' ? 'resource_editorial-content' : item.index,
          }));
        } catch (error) {
          console.log(error);
        }
        this.isLoadingSuggestions = false;
      }, 500);
    },
    splitTitle(title) {
      const escapedQuery = this.getQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // Escape special characters
      const regex = new RegExp(`(${escapedQuery})`, 'gi'); // Case-insensitive match
      const parts = title.split(regex); // Split by query matches

      // Map parts into Objekte mit `text` und `isMatch`, um die Formatierung zu steuern
      return parts.map((part) => ({
        text: part,
        isMatch: part.toLowerCase() === this.getQuery.toLowerCase(),
      }));
    }
  },
  created() {
    let query = this.$route.query.query;
    this.setQuery(query || "");



    const cookieConsent = this.getCookie('cookie_consent_drupal');
    
    if (cookieConsent === 'true' || cookieConsent === '2' || cookieConsent === '0') {
      // Nur wenn akzeptiert: Bounce-Cookie prüfen/setzen
      const hasSeenBounce = this.getCookie('filter-bounce-shown');
      if (!hasSeenBounce) {
        this.shouldBounce = true;
        this.setCookie('filter-bounce-shown', 'true', 365);
      }
    } else {
      // Fallback: Bounce immer anzeigen wenn keine Cookie-Zustimmung
      this.shouldBounce = true;
    }

  },
  mounted() {
    this.$nextTick(() => {
      $('[data-toggle="tooltip"]').tooltip({
        container: "body",
        html: true,
        trigger: 'hover',
        delay: { show: 100, hide: 1200 }, // Verzögerung beim Ausblenden
      });

      // Event-Listener für Tooltip-Links
      $(document).on('click', '.tooltip a', function () {
        // Link öffnen und Tooltip schließen
        const url = $(this).attr('href');
        window.open(url, '_blank');
        $('.tooltip').tooltip('hide');
      });
    });
  }
}
</script>

<style lang="scss">
/* Globaler Style für Tooltips - NICHT scoped */
.tooltip .tooltip-inner a,
.tooltip .tooltip-inner .tooltip-link {
  color: #fff !important;
  text-decoration: underline !important;
}

.tooltip .tooltip-inner a:hover,
.tooltip .tooltip-inner .tooltip-link:hover {
  color: #ffffff !important;
  text-decoration: underline !important;
}
</style>

<style lang="scss" scoped>
.filter-btn {
  position: absolute;
  top: 9px;
  right: 45px;
  background-color: #F7F7F7;
  border-radius: 30px;
  border: none;
  padding: 3px 15px;
  transition: background-color 0.3s, color 0.3s, transform 0.2s;
}

.filter-btn-active {
  background-color: #0e47cb;
  color: #fff;
  margin-left: 0.1rem;
}

.filter-btn svg {
  padding-bottom: 2px;
}

.bounce-animation {
  animation: gentle-bounce 2s ease-in-out 2;
}

@keyframes gentle-bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-8px) scale(1.05);
  }
  60% {
    transform: translateY(-4px) scale(1.02);
  }
}
.focused {
  background-color: #f5f5f5;
  color: #0e47cb;
  margin-left: 0.1rem;
}


.link {
  text-decoration: none;
  color: transparent;
}

router-link:focus {
  outline: none;
}

.form-control input[data-v-5fdd3dbc]:focus {
  border-color: none !important;
}

a:active {
  border: none !important;
}

.search-in {
  position: absolute;
  bottom: -45px;
  background-color: #515560 !important;
  color: #fff !important;
  padding: 6px 10px 5px 10px !important;
  font-size: 14px;
  font-weight: 100;
  border-radius: 30px;
}

.material-icons.small-icon {
  font-size: 20px;
}

.form-control {
  position: relative;
  border: none !important;
  padding: 0;
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;

  .tooltip-icon {
    font-size: 15px;
    position: absolute;
    right: 15px;
    z-index: 2;
    top: 14px;
  }

  .tooltip-icon {
    font-size: 15px;
  }

  span {
    font-family: Arial, sans-serif;
    font-size: medium;
    color: #2c2c2c;
  }

  div {
    margin-top: 4px;
    padding: 0;
    font-family: Arial, sans-serif;
  }

  .suggestedTitlesWrapper {
    position: absolute;
    z-index: 99;
    background-color: white;
    padding: 0;
    width: 100%;
    border-right: 1px solid #c0c0c0;
    border-left: 1px solid #c0c0c0;
    border-bottom: 1px solid #c0c0c0;


    li {
      padding: 0.7rem;
      list-style: none;
      cursor: pointer;
      transition: margin 100ms ease-in-out;

      &:hover {
        color: #0e47cb;
        margin-left: 0.1rem;
        background-color: #f5f5f5;
      }
    }
  }

  .searchbar-size {
    flex-grow: 6;
  }

 input {
    display: block;
    width: 100% !important;
    height: 100%;
    margin: 0 !important;
    padding: .175rem 10.5rem .175rem .75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #c0c0c0 !important;
    border-right: none !important;
    border-radius: 0.25rem 0.1rem 0.1rem 0.25rem;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out !important;

    &:focus {
      border-color: #4A82FA;
      outline: none;
    }
  }
}

.loadingField:nth-child(odd) {

  overflow: hidden;
  animation: slide 1s infinite;
  background-image: linear-gradient(to right, #cecece, transparent);
  background-size: 200% 100%;

  &:hover {
    margin: 0 !important;
  }
}

@keyframes slide {
  0% {
    background-position: 0%;
  }

  50% {
    background-position: 100%;
  }

  100% {
    background-position: 0%;
  }
}

.loadingField {
  color: rgba(0, 0, 0, 0.0);

  &:hover {
    color: rgba(0, 0, 0, 0.0) !important;
  }
}

.auto-suggestion-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auto-content {
  flex: 1;
}

.auto-thumbnail {
  background-color: #F7F7F7;
  border-radius: 4px;
  width: 45px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.auto-image {
  max-height: 30px;
  max-width: 45px;
  border-radius: 4px;
  flex-shrink: 0;
}

.auto-image-resource {
  // max-height: 60px;
  max-width: 80px;
  border-radius: 4px;
  flex-shrink: 0;
}

.auto-thumbnail svg {
  position: static;
}

.auto-title {
  color: #404040;
}

.auto-type {
  font-size: 14px;
  color: #949494;
}

.auto-type::first-letter {
  text-transform: uppercase;
}

.material-icons.small-icon {
  font-size: 20px;
}

@media screen and (max-width: 767.5px) {


  .form-control {
    border: none;
     width: 350px !important;
 
    input {
      border: 1px solid #4A82FA !important;
      border-radius: 4px;
    }
  }
}


</style>
